import React, {  Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray, FormikHandlers, FormikErrors, FormikTouched, FieldArrayRenderProps } from 'formik';
import styled from 'styled-components/macro';

import DiscountTypeSelector from './DiscountTypeSelector';
import Promotion from './Promotion/Promotion';
import LoyaltyDiscount from './LoyaltyDiscount/LoyaltyDiscount';
import {
    Campaign,
    DiscountType,
    DiscountItem,
    Requirement,
    AssetRequestObject,
    Stage
} from '../../types';
import { CampaignCard, NextButton } from '../../common/components';
import { Button } from '../../../components/Buttons';
import { distances } from '../../../styles/constants';

interface DiscountProps {
    values: Campaign;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleChange: FormikHandlers['handleChange'];
    handleBlur: FormikHandlers['handleBlur'];
    errors: FormikErrors<Campaign>;
    touched: FormikTouched<Campaign>;
    onNextStage: () => void;
    createAsset: (asset: AssetRequestObject) => void;
    removeAsset: (discountId: string) => void;
    isEdit?: boolean;
    discountToEditCount?: number;
    scopes: string[];
    assetsByDiscountId: { [discountId: string]: string };
    isMccOnly?: boolean;
    emptyDiscount?: DiscountItem;
}

const Discount = (
    {
        values,
        setFieldValue,
        handleChange,
        handleBlur,
        errors,
        touched,
        onNextStage,
        createAsset,
        removeAsset,
        isEdit,
        discountToEditCount,
        scopes,
        assetsByDiscountId,
        isMccOnly,
        emptyDiscount,
    }: DiscountProps
) => {
    const { t } = useTranslation();

    const onNextClick = () => {
        const isCampaignError = !Object.values(errors).some(Boolean);
        const discountsErrors = (errors.discounts as FormikErrors<DiscountItem>[]) || [];
        const isDiscountsError = discountsErrors.every((errors) => Object.entries(errors).length === 0);
        const isRequirementsError = discountsErrors.every((errors) =>
            ((errors.requirements as FormikErrors<Requirement>[]) || []).every(
                (requirementError) => Object.entries(requirementError).length === 0
            ));
        if (isCampaignError && isDiscountsError && isRequirementsError) {
            onNextStage();
        }
    };

    const isEditable = (discountIndex: number) => (discountToEditCount ? discountIndex >= discountToEditCount : true);

    const checkIfScheduledOrActive = () => {
        const toDate = new Date(values.dateTo);
        toDate.setHours(values.timeTo.getHours(), values.timeTo.getMinutes());
        const currentTimestamp = new Date().getTime();
        const dateToTimestamp = toDate.getTime();
        return !isEdit || currentTimestamp < dateToTimestamp;
    };

    const addDiscount = (arrayHelpers: FieldArrayRenderProps | DiscountItem[]) => {
        if (emptyDiscount) {
            arrayHelpers.push(emptyDiscount);
        }
    };

    return (
        <FieldArray
            name="discounts"
            render={(arrayHelpers) => (
                <Fragment>
                    {values.discounts.map((discount, index) => (
                        <CampaignCard
                            title={`${t('marketing.campaign.discount_count', { count: index + 1 })} ${
                                discount.title ? `: ${discount.title}` : ''
                            }`}
                            shortHr
                            marginBottom={distances.small}
                            key={index}
                            onDuplicate={() => arrayHelpers.push(discount)}
                            onRemove={() => arrayHelpers.remove(index)}
                        >
                            <DiscountTypeSelector
                                discount={discount}
                                index={index}
                                setFieldValue={setFieldValue}
                                isEditable={isEditable(index)}
                                isMccOnly={isMccOnly}
                            />
                            {discount.type === DiscountType.loyalty && (
                                <LoyaltyDiscount
                                    discount={discount}
                                    index={index}
                                    setFieldValue={setFieldValue}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    touched={touched}
                                    errors={errors}
                                    createAsset={createAsset}
                                    removeAsset={removeAsset}
                                    isEditable={isEditable(index)}
                                    isScheduledOrActive={checkIfScheduledOrActive()}
                                    scopes={scopes}
                                    asset={assetsByDiscountId[discount.id]}
                                />
                            )}
                            {discount.type !== DiscountType.loyalty && (
                                <Promotion
                                    discount={discount}
                                    index={index}
                                    setFieldValue={setFieldValue}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    touched={touched}
                                    errors={errors}
                                    createAsset={createAsset}
                                    removeAsset={removeAsset}
                                    isEditable={isEditable(index)}
                                    isScheduledOrActive={checkIfScheduledOrActive()}
                                    scopes={scopes}
                                    asset={assetsByDiscountId[discount.id]}
                                    isMccOnly={isMccOnly}
                                />
                            )}

                        </CampaignCard>
                    ))}
                    <ActionButtonsWrapper>
                        <Button type="button" onClick={() => addDiscount(arrayHelpers)}>
                            {t('marketing.campaign.add_discount')}
                        </Button>
                        {!isEdit && values.discounts.length > 0 && (
                            <NextButton type="submit" onClick={onNextClick}>
                                {t('marketing.campaign.next')}
                            </NextButton>
                        )}
                    </ActionButtonsWrapper>
                </Fragment>
            )}
        />
    );
};

const ActionButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 1200px;
    margin-bottom: ${distances.normal};
`;

export default Discount;
